/**
 * APIクライアント
 *
 * LaravelでいうHttp Facadeに相当
 * API呼び出しを一元管理し、共通の設定（ベースURL、ヘッダー、エラー処理）を提供
 */

// APIのベースURL
// Viteプロキシ経由で /api/* → サーバーに転送されるため、空文字（相対パス）でOK
// 直接サーバーに接続したい場合は VITE_API_URL=http://localhost:3000 を設定
const API_BASE_URL = import.meta.env.VITE_API_URL || '';

/**
 * APIエラークラス
 * LaravelのHttpExceptionに相当
 */
export class ApiError extends Error {
  constructor(
    public status: number,
    public statusText: string,
    message: string
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * APIリクエストのオプション型
 */
interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  body?: unknown;
  headers?: Record<string, string>;
}

/**
 * 共通のfetch関数
 *
 * LaravelでいうHttp::get(), Http::post()などに相当
 *
 * @param endpoint - APIエンドポイント（例: '/api/facilities'）
 * @param options - リクエストオプション
 * @returns レスポンスデータ
 */
async function request<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, headers = {} } = options;

  // リクエスト設定
  const config: RequestInit = {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...headers,
    },
  };

  // ボディがある場合はJSONに変換
  if (body) {
    config.body = JSON.stringify(body);
  }

  // 認証トークンがあれば追加（後で実装）
  const token = localStorage.getItem('token');
  if (token) {
    config.headers = {
      ...config.headers,
      Authorization: `Bearer ${token}`,
    };
  }

  // APIリクエスト実行
  const response = await fetch(`${API_BASE_URL}${endpoint}`, config);

  // エラーレスポンスの処理
  if (!response.ok) {
    // エラーメッセージを取得
    let errorMessage = response.statusText;
    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorData.error || errorMessage;
    } catch {
      // JSONパースに失敗した場合はstatusTextを使用
    }

    throw new ApiError(response.status, response.statusText, errorMessage);
  }

  // 204 No Content の場合は空オブジェクトを返す
  if (response.status === 204) {
    return {} as T;
  }

  // JSONレスポンスをパース
  return response.json();
}

/**
 * APIクライアント
 *
 * 使用例:
 * const facilities = await api.get<Facility[]>('/api/facilities');
 * const patient = await api.post<Patient>('/api/patients', { name: '田中太郎' });
 */
export const api = {
  /**
   * GETリクエスト
   * Laravel: Http::get('/api/facilities')
   */
  get: <T>(endpoint: string, headers?: Record<string, string>) =>
    request<T>(endpoint, { method: 'GET', headers }),

  /**
   * POSTリクエスト
   * Laravel: Http::post('/api/patients', $data)
   */
  post: <T>(endpoint: string, body?: unknown, headers?: Record<string, string>) =>
    request<T>(endpoint, { method: 'POST', body, headers }),

  /**
   * PUTリクエスト
   * Laravel: Http::put('/api/patients/1', $data)
   */
  put: <T>(endpoint: string, body?: unknown, headers?: Record<string, string>) =>
    request<T>(endpoint, { method: 'PUT', body, headers }),

  /**
   * DELETEリクエスト
   * Laravel: Http::delete('/api/patients/1')
   */
  delete: <T>(endpoint: string, headers?: Record<string, string>) =>
    request<T>(endpoint, { method: 'DELETE', headers }),

  /**
   * PATCHリクエスト
   * Laravel: Http::patch('/api/patients/1', $data)
   */
  patch: <T>(endpoint: string, body?: unknown, headers?: Record<string, string>) =>
    request<T>(endpoint, { method: 'PATCH', body, headers }),
};

/**
 * CSVファイルをダウンロード
 *
 * Blobとしてレスポンスを取得し、ブラウザのダウンロードダイアログを起動
 *
 * @param examId - 検査セッションID
 * @param filename - ダウンロード時のファイル名
 */
export async function downloadCsv(examId: string, filename: string): Promise<void> {
  const token = localStorage.getItem('token');
  const response = await fetch(`${API_BASE_URL}/api/exams/${examId}/csv`, {
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });

  if (!response.ok) {
    let errorMessage = response.statusText;
    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorData.error || errorMessage;
    } catch {
      // JSONパースに失敗した場合はstatusTextを使用
    }
    throw new ApiError(response.status, response.statusText, errorMessage);
  }

  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  window.URL.revokeObjectURL(url);
}

/**
 * 患者の検査結果をZIPファイルとして一括ダウンロード
 *
 * @param patientId - 患者ID
 * @param filename - ダウンロード時のファイル名
 */
export interface ExportFilter {
  from?: string;
  to?: string;
  lineLengths?: number[];
}

export async function downloadZip(patientId: string, filename: string, filter?: ExportFilter): Promise<void> {
  const token = localStorage.getItem('token');
  const params = new URLSearchParams();
  if (filter?.from) params.set('from', filter.from);
  if (filter?.to) params.set('to', filter.to);
  if (filter?.lineLengths && filter.lineLengths.length > 0) {
    params.set('lineLengths', filter.lineLengths.join(','));
  }
  const qs = params.toString();
  const response = await fetch(`${API_BASE_URL}/api/exams/patient/${patientId}/export${qs ? `?${qs}` : ''}`, {
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });

  if (!response.ok) {
    let errorMessage = response.statusText;
    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorData.error || errorMessage;
    } catch {
      // JSONパースに失敗した場合はstatusTextを使用
    }
    throw new ApiError(response.status, response.statusText, errorMessage);
  }

  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  window.URL.revokeObjectURL(url);
}

export default api;
